/**
 * The initials disc. Four call sites shared this markup in M1a (the dock card,
 * a result seat, the player row, the matched reveal) — the double ring
 * `0 0 0 2px surface, 0 0 0 3px line2` is the design's, not a scale of mine.
 *
 * `size` is the disc: sm 32 (a dock card), md 40 (a result seat), lg 44 (the
 * matched reveal) and `row` 36 (the Stage's player row since the clean-up of
 * 2026-10-05 / R6.3, which has no token of its own because `theme/tokens.ts` is
 * the shared core). `compact` is the player row's tighter initials.
 */
import React from 'react';
import {StyleSheet, View} from 'react-native';

import {SIZE} from '../theme/tokens';
import {useV2Theme} from '../theme/useV2Theme';
import {initialsOf} from '../components/initials';
import Txt, {TxtVariant} from './Txt';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'row';

/** the player row's disc (R6.3) */
const ROW_DIAMETER = 36;
/** `ev` is me, `alt` is the opponent (tokens.ev / tokens.alt) */
export type AvatarTone = 'ev' | 'alt';

const DIAMETER: {[K in AvatarSize]: number} = {
  sm: SIZE.avatarSm,
  md: SIZE.avatarMd,
  lg: SIZE.avatarLg,
  row: ROW_DIAMETER,
};

const INITIALS: {[K in AvatarSize]: TxtVariant} = {
  sm: 'initialsSm',
  md: 'initialsMd',
  lg: 'initialsLg',
  row: 'initialsRow',
};

export type AvatarProps = {
  name: string;
  size?: AvatarSize;
  tone?: AvatarTone;
  /** the player row's denser initials */
  compact?: boolean;
};

function Avatar({name, size = 'sm', tone = 'alt', compact}: AvatarProps) {
  const tokens = useV2Theme(s => s.tokens);
  const diameter = DIAMETER[size];
  return (
    <View
      style={[
        styles.disc,
        {
          width: diameter,
          height: diameter,
          borderRadius: diameter / 2,
          backgroundColor: tone === 'ev' ? tokens.ev : tokens.alt,
          boxShadow: `0 0 0 2px ${tokens.surface}, 0 0 0 3px ${tokens.line2}`,
        } as any,
      ]}>
      <Txt
        variant={compact ? 'initialsRow' : INITIALS[size]}
        tone={tone === 'ev' ? 'onEv' : 'onAlt'}>
        {initialsOf(name)}
      </Txt>
    </View>
  );
}

const styles = StyleSheet.create({
  disc: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default React.memo(Avatar);
